// Centralized fetch helper
//
// Purpose:
// - Centralize HTTP requests, JSON body handling, Authorization header injection, and common error handling.
// - Keep API calls consistent and avoid duplicated auth logic across the app.

import { ensureReauth, getAccessTokenSilent } from '../auth/msal';

export interface HttpOptions extends RequestInit { json?: any; auth?: boolean; }

async function getAccessToken(): Promise<string | null> {
  try { return await getAccessTokenSilent(); } catch { return null; }
}

async function triggerReauth() {
  try { await ensureReauth(false); } catch { /* ignore */ }
}

export async function http<T = any>(url: string, options: HttpOptions = {}): Promise<T> {
    const { json, headers, auth = true, ...rest } = options;
    const init: RequestInit = {
        ...rest,
        headers: { 'Content-Type': 'application/json', ...(headers || {}) }
    };
    if (json !== undefined) init.body = JSON.stringify(json);

    if (auth) {
        let token = await getAccessToken();
        if (!token) {
            // Attempt to re-authenticate and do NOT send the request without a token
            await triggerReauth();
            // Throw an auth error to stop current flow; app will redirect and reload
            throw new Error('401 Unauthorized');
        }
        (init.headers as any)['Authorization'] = `Bearer ${token}`;
    }

    const res = await fetch(url, init);

    if (auth && res.status === 401) {
        // Token likely expired or invalid; kick off re-auth and surface error
        await triggerReauth();
        throw new Error('401 Unauthorized');
    }

    if (res.status === 403) throw new Error('Not authorized. Ask an administrator to add your email address.');
    const ct = res.headers.get('content-type') || '';

    // Read body exactly once
    const raw = await res.text();
    let parsed: any = null;
    const looksJson = ct.includes('application/json');

    if (looksJson && raw) {
        try {
            parsed = JSON.parse(raw);
        } catch (err) {
            // Debug help: you can temporarily log raw + error if needed
            console.warn('http(): failed to parse JSON response', err, raw);
        }
    }

    if (!res.ok) {
        // Prefer JSON payload if available
        if (parsed && typeof parsed === 'object') {
            const serverMessage = parsed.message || parsed.error || `${res.status} ${res.statusText}`;
            const err: any = new Error(serverMessage);
            err.payload = parsed;
            err.status = res.status;
            throw err;
        }

        // Fallback: use raw text
        const msg = raw
            ? `${res.status} ${res.statusText}: ${raw}`
            : `${res.status} ${res.statusText}`;
        throw new Error(msg);
    }

    // Success path
    if (parsed && typeof parsed === 'object') {
        return parsed as T;
    }

    // Non-JSON success, return raw text
    return raw as any as T;
}